'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { pauseAdResponseSchema } from '@borradh-workspace/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ListAdsResponse } from '../types';

export const usePauseAd = (toastOnSuccess = true, toastOnError = true) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (adId: string) => {
      return apiClient.post<{ paused: true }>(
        `meta-ads/${adId}/pause`,
        undefined,
        { schema: pauseAdResponseSchema }
      );
    },
    onMutate: async (adId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.metaAds.all() });
      const previous = queryClient.getQueriesData<ListAdsResponse>({
        queryKey: queryKeys.metaAds.all(),
      });
      queryClient.setQueriesData<ListAdsResponse>(
        { queryKey: queryKeys.metaAds.all() },
        (old) => {
          if (!old?.ads) return old;
          return {
            ...old,
            ads: old.ads.map((ad) =>
              ad.id === adId ? { ...ad, status: 'paused' as const } : ad
            ),
          };
        }
      );
      return { previous };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.metaAds.all() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.metaAds.campaigns(),
      });
      if (toastOnSuccess) toast.success('Ad paused');
    },
    onError: (error, _adId, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
      if (toastOnError) toast.error(`Failed to pause ad: ${error.message}`);
    },
  });

  return {
    ...mutation,
    execute: mutation.mutate,
    executeAsync: mutation.mutateAsync,
    isExecuting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
